import { GAME_HAND_PHASE } from 'constants/gameConstants';

import { avatarsLabel, StatusAvatarLookup, withRewards } from 'components/Streaks/model/milestones';
import { StreakSnapshot } from 'components/Streaks/model/types';

/**
 * Which table moment to play for a snapshot's `moment` (EN-11163). Pure, so
 * the rules are unit-tested apart from the timing.
 */
export type BurstKind = 'daily' | 'milestone' | 'first';

export interface MomentPlan {
  /** `${day}:${handId}`: a moment plays once, ever, for this key. */
  key: string;
  /** This felt is the table where the day counted. */
  countedHere: boolean;
  /** Beat one, the chip at the qualifying flop. Skipped for a pre-flop win. */
  chip: boolean;
  /** Beat two, after the winner is shown. */
  burst: BurstKind;
  days: number;
  /** Milestone reached today, for the ribbon and the reward chip. */
  milestoneName?: string;
  rewardLine?: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The moment's identity: one per qualifying day per player. */
export const momentKey = (snap: StreakSnapshot | null): string | null =>
  snap?.moment ? `${snap.day}:${snap.moment.handId}` : null;

/**
 * The moment as any felt would play it. It is owed to the player, not to the
 * table where the day counted (AC 5 to 7): whichever felt on screen claims a
 * beat plays it. Table ids repeat across game types, so `countedHere` matches
 * both.
 */
export function planMoment(
  snap: StreakSnapshot | null,
  tableGameType: string | undefined,
  tableGameId: string | number | undefined,
  statusAvatars?: StatusAvatarLookup
): MomentPlan | null {
  const key = momentKey(snap);
  if (!snap?.moment || !key || tableGameId === undefined || tableGameId === '') return null;
  const days = snap.currentStreak;
  const milestones = withRewards(snap.milestones, statusAvatars);
  const milestone =
    snap.moment.kind === 'milestone'
      ? milestones.find((m) => m.key === snap.notice?.milestoneReached?.key) ||
        milestones.find((m) => m.days === days)
      : undefined;
  let burst: BurstKind = 'daily';
  if (milestone) burst = 'milestone';
  // A streak's first day, new or a restart (EN-11163 AC 4); the server marks it.
  else if (snap.moment.kind === 'first' || (days === 1 && snap.bestStreak <= 1)) burst = 'first';
  return {
    key,
    countedHere:
      snap.moment.gameType === tableGameType && String(snap.moment.gameId) === String(tableGameId),
    chip: !snap.moment.wonPreflop,
    burst,
    days,
    milestoneName: milestone?.name,
    rewardLine: milestone
      ? capitalize(
          `${avatarsLabel(milestone)} · ${plural(milestone.freezes, 'freeze')} · ${
            milestone.name
          } frame`
        )
      : undefined
  };
}

/**
 * The owed moment, shared by every felt (EN-11163 build note). Each beat is
 * claimed once per moment by the first felt that may play it; claiming clears
 * it, so a re-push, a remount or a table switch never replays it. A claim made
 * while waiting for the next hand is released if that felt leaves the screen,
 * so the next free felt on screen takes it instead.
 */
export type Beat = 'chip' | 'burst';

const claimed = new Set<string>();
const firstSeen = new Map<string, number>();

export function claimBeat(key: string, beat: Beat): boolean {
  const id = `${key}:${beat}`;
  if (claimed.has(id)) return false;
  claimed.add(id);
  return true;
}

export function releaseBeat(key: string, beat: Beat) {
  claimed.delete(`${key}:${beat}`);
}

export const isBeatClaimed = (key: string, beat: Beat): boolean => claimed.has(`${key}:${beat}`);

/** The player went to the lobby before beat two: neither beat plays (AC 5). */
export function dropMoment(key: string) {
  claimed.add(`${key}:chip`);
  claimed.add(`${key}:burst`);
}

/** When this moment first reached a felt; the chip belongs to the flop, not later. */
export function noteMoment(key: string, now = Date.now()): number {
  const seen = firstSeen.get(key);
  if (seen !== undefined) return seen;
  firstSeen.set(key, now);
  return now;
}

/**
 * The felts on screen right now, by game type and id (EN-11376). When the
 * table where the day counted is on screen, the moment is its alone: another
 * felt plays it only while that table can't be seen (Jason, 11/03).
 */
const feltsOnScreen = new Map<string, number>();
const feltListeners = new Set<() => void>();

export const feltKey = (gameType: string | undefined, gameId: string | number | undefined) =>
  `${gameType ?? ''}:${gameId ?? ''}`;

/** Marks a felt on screen until the returned function is called. */
export function markFeltOnScreen(key: string): () => void {
  feltsOnScreen.set(key, (feltsOnScreen.get(key) ?? 0) + 1);
  feltListeners.forEach((l) => l());
  return () => {
    const n = (feltsOnScreen.get(key) ?? 1) - 1;
    if (n > 0) feltsOnScreen.set(key, n);
    else feltsOnScreen.delete(key);
    feltListeners.forEach((l) => l());
  };
}

export const isFeltOnScreen = (key: string): boolean => feltsOnScreen.has(key);

export function subscribeFeltsOnScreen(listener: () => void): () => void {
  feltListeners.add(listener);
  return () => {
    feltListeners.delete(listener);
  };
}

export function resetPlayedMoments() {
  claimed.clear();
  firstSeen.clear();
}

/**
 * Whether the player is free at this felt: watching it, folded, sitting out or
 * between hands. A free felt on screen plays beat two right away (AC 6, 5M).
 */
export function isFreeAtTable({
  isSeated,
  isWaiting,
  isFolded,
  hand
}: {
  isSeated: boolean;
  isWaiting: boolean;
  isFolded: boolean;
  hand: number | string | undefined;
}): boolean {
  if (!isSeated || isWaiting || isFolded) return true;
  const phase = Number(hand);
  return (
    !Number.isFinite(phase) ||
    phase < GAME_HAND_PHASE.DEAL_CARDS ||
    phase >= GAME_HAND_PHASE.RECORD_NEW_STATS_AND_START_NEW_HAND
  );
}

/** Beat timings (EN-11163 Details). */
/**
 * Doubled from 1.8 s so the chip is easier to catch (JRam 10/29), and again
 * after the product review (11/04). Beat two cuts it short if it starts first.
 */
export const CHIP_MS = 7200;
/** The reference's lengths: 40% of its 6, 7 and 8 s cycles. */
export const BURST_MS: Record<BurstKind, number> = { daily: 2400, first: 2800, milestone: 3200 };
/**
 * Beat two waits for the next hand after the winner is shown. With no next
 * hand by then (the player left, the table broke) it plays anyway. Longer than
 * the winner step plus the hold at the slowest hand pace (3 s + 2 s).
 */
export const NEXT_HAND_WAIT_MS = 6000;
/**
 * Once the next hand begins, beat two waits this long so the last hand's board
 * has swept off the felt (HoldEmCards' 0.4 s sweep) before the count lands
 * where the cards were.
 */
export const BOARD_SWEEP_MS = 450;
/** The chip shows only this soon after the moment lands, so a late felt never shows it. */
export const CHIP_CLAIM_WINDOW_MS = 1000;
/** A burst that started mid-break fades this fast when the next hand's cards reach the seat (AC 5). */
export const DEAL_FADE_MS = 200;
/** A grid cell at least this far in view counts as on screen (AC 7). */
export const GRID_IN_VIEW_FRACTION = 0.5;
